// =============================================================================
// Token ledger — the single source of truth for token balance.
//
// Backed by localStorage with an in-memory cache and a pub/sub layer so React
// (via useTokens) and plain services (assessmentService, careerService, …) can
// all read/charge tokens without circular context dependencies.
//
// Swap the localStorage reads/writes for real backend calls when ready; the
// async signatures already return promises.
// =============================================================================

import type { Transaction, TokenSource } from '../../types/membership';
import { TOKEN_COSTS, type TokenAction } from './tokenCosts';

const STORAGE_KEY = 'teskedo_token_ledger_v1';

/**
 * Thrown by `tokenService.charge()` when the balance can't cover an action.
 * The UI's `runGuarded` wrapper catches this and redirects to the wallet, so AI
 * actions never need to pre-check the balance — the spend is the single source
 * of truth (server-authoritative once wired to a real backend).
 */
export class InsufficientTokensError extends Error {
  readonly required: number;
  readonly balance: number;
  readonly action: string;

  constructor(required: number, balance: number, action: string) {
    super(`Insufficient tokens: need ${required}, have ${balance}`);
    this.name = 'InsufficientTokensError';
    this.required = required;
    this.balance = balance;
    this.action = action;
  }
}

interface LedgerState {
  transactions: Transaction[];
}

let cache: LedgerState | null = null;
const listeners = new Set<() => void>();

function uid(prefix = 'txn'): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function load(): LedgerState {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    cache = raw ? (JSON.parse(raw) as LedgerState) : { transactions: [] };
  } catch {
    cache = { transactions: [] };
  }
  return cache;
}

function persist(state: LedgerState) {
  cache = state;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / private-mode errors — in-memory cache still works
  }
  listeners.forEach((l) => l());
}

function isExpired(txn: Transaction, now = Date.now()): boolean {
  return !!txn.expiry && new Date(txn.expiry).getTime() < now;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface SpendResult {
  ok: boolean;
  balance: number;
}

export const tokenService = {
  /** Current spendable balance (earned-and-unexpired minus spent). */
  getBalance(): number {
    const { transactions } = load();
    const now = Date.now();
    return transactions.reduce((sum, t) => {
      if (t.type === 'spend') return sum - t.amount;
      return isExpired(t, now) ? sum : sum + t.amount;
    }, 0);
  },

  getTransactions(): Transaction[] {
    return [...load().transactions].sort((a, b) => +new Date(b.date) - +new Date(a.date));
  },

  /** Tokens that will expire within `days` (used for "use it or lose it" nudges). */
  getExpiringSoon(days = 7): number {
    const now = Date.now();
    const cutoff = now + days * 86400000;
    return load()
      .transactions.filter(
        (t) => t.type === 'earn' && t.expiry && !isExpired(t, now) && new Date(t.expiry).getTime() <= cutoff,
      )
      .reduce((s, t) => s + t.amount, 0);
  },

  /** Credit tokens. `expiresInDays` omitted = never expires (e.g. purchases). */
  async earn(
    amount: number,
    source: TokenSource,
    label: string,
    expiresInDays?: number,
  ): Promise<number> {
    const state = load();
    const txn: Transaction = {
      id: uid(),
      amount,
      type: 'earn',
      source,
      label,
      date: new Date().toISOString(),
      expiry: expiresInDays ? new Date(Date.now() + expiresInDays * 86400000).toISOString() : undefined,
    };
    persist({ transactions: [...state.transactions, txn] });
    return this.getBalance();
  },

  /** Attempt to debit tokens. Returns ok:false (without charging) if too few. */
  async spend(amount: number, purpose: string): Promise<SpendResult> {
    const balance = this.getBalance();
    if (balance < amount) return { ok: false, balance };
    const state = load();
    const txn: Transaction = {
      id: uid(),
      amount,
      type: 'spend',
      source: purpose,
      label: purpose,
      date: new Date().toISOString(),
    };
    persist({ transactions: [...state.transactions, txn] });
    return { ok: true, balance: this.getBalance() };
  },

  /**
   * Atomically charge for an AI action. Looks up the cost, attempts the debit,
   * and throws InsufficientTokensError (without charging) if the balance is too
   * low. This is the ONE place an AI action's tokens are spent.
   */
  async charge(action: TokenAction, label?: string): Promise<number> {
    const cost = TOKEN_COSTS[action];
    const result = await this.spend(cost, label ?? action.replace(/_/g, ' '));
    if (!result.ok) throw new InsufficientTokensError(cost, result.balance, action);
    return result.balance;
  },

  async checkBalance(): Promise<number> {
    return this.getBalance();
  },

  /** Subscribe to balance/ledger changes. Returns an unsubscribe fn. */
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  /** Test/demo helper — clears the ledger. */
  reset() {
    persist({ transactions: [] });
  },
};
